<template>
  <b-container class="metric-info">
    <b-row v-if="category.label">
      <b-col md="1" v-if="category.image">
        <img
          class="category-image"
          :src="getImageUrl('culture', `${category.image}.svg`)"
        />
      </b-col>
      <b-col md="1" v-else>
        <img
          :src="getImageUrl('custom-surveys', 'no-image.png')"
          class="category-image"
        />
      </b-col>
      <b-col md="11">
        <h4>{{ category.label }}</h4>
        <p class="mt-1" v-if="category.description">
          {{ category.description }}
        </p>
      </b-col>
    </b-row>
    <b-row class="pt-3" v-if="category.score !== null">
      <b-col fluid class="metric-score">
        <Overall :score="category.score" class="pt-3" />
        <HistoricChart :history="category.history" />
      </b-col>
    </b-row>
    <b-row class="pt-3">
      <b-col fluid>
        <div
          class="metric-questions"
          v-for="(question, index) in category.questions"
          :key="question.question_id"
        >
          <b-row v-if="index === 0" class="titles">
            <b-col md="10">
              <small class="d-none d-md-block">
                {{ labels.reliabilityRate.questions }}
              </small>
            </b-col>
            <b-col md="2">
              <small class="d-none d-md-block text-center">
                {{ labels.reliabilityRate.title1 }}
                <br />
                {{ labels.reliabilityRate.title2 }}
              </small>
            </b-col>
          </b-row>
          <b-row>
            <b-col md="10" class="question-item pb-1">
              <div class="question-label">
                <img
                  :title="labels.questions.type[question.type]"
                  :src="getImageUrl('custom-surveys', `${question.type}.svg`)"
                  class="category-icon"
                />
                <b-link
                  v-if="question.type === 'feedback'"
                  :title="labels.seeAnswers"
                  @click="goToFeedbacks(question)"
                >
                  <small>{{ question.text }}</small>
                </b-link>
                <small v-else>{{ question.text }}</small>
                <i
                  v-if="question.enableScore"
                  :title="labels.enabledScore"
                  class="mdi mdi-trophy-variant ml-2 text-warning"
                >
                </i>
              </div>
              <b-progress
                v-if="
                  ['slide-number', 'nps', 'stars'].includes(question.type) ||
                    (question.type === 'select' && question.enableScore)
                "
                :max="100"
                show-value
                animated
                class="mb-1"
              >
                <!-- skip -->
                <b-progress-bar
                  :id="'question-info' + question.question_id"
                  :value="getQuestionValue(question.frequencies, 'skip')"
                  variant="info"
                >
                  {{ getQuestionValue(question.frequencies, 'skip') }}%
                </b-progress-bar>
                <b-tooltip :target="'question-info' + question.question_id">
                  <div class="tooltip-info">
                    {{ labels.status.skip }}
                    {{ getQuestionValue(question.frequencies, 'skip') }}%
                  </div>
                </b-tooltip>
                <!-- very unhappy -->
                <b-progress-bar
                  :id="'question-red' + question.question_id"
                  :value="getQuestionValue(question.frequencies, -2)"
                  variant="red10"
                >
                  {{ getQuestionValue(question.frequencies, -2) }}%
                </b-progress-bar>
                <b-tooltip :target="'question-red' + question.question_id">
                  <div class="tooltip-info">
                    {{ labels.status.veryLow }}
                    {{ getQuestionValue(question.frequencies, -2) }}%
                  </div>
                </b-tooltip>
                <!-- unhappy -->
                <b-progress-bar
                  :id="'question-red02' + question.question_id"
                  :value="getQuestionValue(question.frequencies, -1)"
                  variant="red09"
                >
                  {{ getQuestionValue(question.frequencies, -1) }}%
                </b-progress-bar>
                <b-tooltip :target="'question-red02' + question.question_id">
                  <div class="tooltip-info">
                    {{ labels.status.low }}
                    {{ getQuestionValue(question.frequencies, -1) }}%
                  </div>
                </b-tooltip>
                <!-- neutral -->
                <b-progress-bar
                  :id="'question-warning' + question.question_id"
                  :value="getQuestionValue(question.frequencies, 0)"
                  variant="warning"
                >
                  {{ getQuestionValue(question.frequencies, 0) }}%
                </b-progress-bar>
                <b-tooltip :target="'question-warning' + question.question_id">
                  <div class="tooltip-info">
                    {{ labels.status.medium }}
                    {{ getQuestionValue(question.frequencies, 0) }}%
                  </div>
                </b-tooltip>
                <!-- happy -->
                <b-progress-bar
                  :id="'question-success' + question.question_id"
                  :value="getQuestionValue(question.frequencies, 1)"
                  variant="green"
                >
                  {{ getQuestionValue(question.frequencies, 1) }}%
                </b-progress-bar>
                <b-tooltip :target="'question-success' + question.question_id">
                  <div class="tooltip-info">
                    {{ labels.status.high }}
                    {{ getQuestionValue(question.frequencies, 1) }}%
                  </div>
                </b-tooltip>
                <!-- very happy -->
                <b-progress-bar
                  :id="'question-green' + question.question_id"
                  :value="getQuestionValue(question.frequencies, 2)"
                  variant="success"
                >
                  {{ getQuestionValue(question.frequencies, 2) }}%
                </b-progress-bar>
                <b-tooltip :target="'question-green' + question.question_id">
                  <div class="tooltip-info">
                    {{ labels.status.veryHigh }}
                    {{ getQuestionValue(question.frequencies, 2) }}%
                  </div>
                </b-tooltip>
                <!-- not answered -->
                <b-progress-bar
                  v-if="question.frequencies.length === 0"
                  :value="100"
                  variant="gray02"
                >
                  {{ labels.status.notAnswered }}
                </b-progress-bar>
              </b-progress>
              <div
                v-if="
                  ['select', 'multi-select'].includes(question.type) &&
                    !question.enableScore
                "
                class="question-answers ml-4"
              >
                <b-row
                  v-for="(item, idx) in question.frequencies"
                  :key="`frequency-item-${question.question_id}-${idx}`"
                >
                  <b-col md="3">
                    <span :class="item.score === 'skip' ? 'text-primary' : ''">
                      {{
                        item.score === 'skip' ? labels.status.skip : item.score
                      }}
                    </span>
                  </b-col>
                  <b-col md="9">
                    <b-progress :max="100" show-value animated>
                      <b-progress-bar
                        v-if="item.frequency === 0"
                        :value="100"
                        variant="gray02"
                        :id="`frequency-answer-${question.question_id}-${idx}`"
                      >
                        {{ labels.status.notAnswered }}
                      </b-progress-bar>
                      <b-progress-bar
                        v-else
                        :id="`frequency-answer-${question.question_id}-${idx}`"
                        :value="
                          getQuestionValue(question.frequencies, item.score)
                        "
                        variant="secondary"
                      >
                        {{ getQuestionValue(question.frequencies, item.score) }}
                        %
                      </b-progress-bar>
                      <b-tooltip
                        :target="
                          `frequency-answer-${question.question_id}-${idx}`
                        "
                      >
                        <div class="tooltip-info">
                          {{ item.score === 'skip' ? 'Pulou' : item.score }}
                          {{
                            getQuestionValue(question.frequencies, item.score)
                          }}
                          %
                        </div>
                      </b-tooltip>
                    </b-progress>
                  </b-col>
                </b-row>
              </div>
            </b-col>
            <b-col
              md="2"
              class="d-none d-md-block reliability-rate text-center mt-3"
            >
              <small>
                <span v-if="question.type === 'feedback'">
                  <b-link
                    v-if="question.type === 'feedback'"
                    :title="labels.seeAnswers"
                    class="questions-link"
                    @click="goToFeedbacks(question)"
                  >
                    {{ labels.seeAnswers }}
                  </b-link>
                </span>
                <span :class="question.reliability_rate" v-else>
                  {{ labels.reliabilityRate[question.reliability_rate] }}
                </span>
              </small>
            </b-col>
            <b-col md="12" class="d-block d-md-none reliability-rate pb-3">
              <small>
                {{ labels.reliabilityRate.title1 }}
                {{ labels.reliabilityRate.title2 }}
                <span v-if="question.type === 'feedback'">
                  <b-link
                    v-if="question.type === 'feedback'"
                    :title="labels.seeAnswers"
                    class="questions-link"
                    @click="goToFeedbacks(question)"
                  >
                    {{ labels.seeAnswers }}
                  </b-link>
                </span>
                <span :class="question.reliability_rate" v-else>
                  {{ labels.reliabilityRate[question.reliability_rate] }}
                </span>
              </small>
            </b-col>
          </b-row>
        </div>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import Overall from '@/components/CustomSurveys/Reports/Overall.vue'
import HistoricChart from '@/components/CustomSurveys/Reports/HistoricChart.vue'
import { mapGetters } from 'vuex'

const props = {
  category: {
    require: true,
    type: Object,
  },
}

export default {
  components: { Overall, HistoricChart },
  props: props,
  computed: {
    ...mapGetters({
      resources: 'resources',
    }),
    labels() {
      return {
        ...this.resources.component.customSurveys.create,
        ...this.resources.component.customSurveys.reports.questions,
        reliabilityRate: {
          ...this.resources.page.reports.engagement.metric.reliabilityRate,
          ...this.resources.component.customSurveys.reports.reliabilityRate,
        },
      }
    },
  },
  methods: {
    goToFeedbacks(question) {
      this.$store.dispatch(
        'customSurveyResults/updateSelectedQuestionId',
        question.question_id
      )
      this.$router.push({
        name: 'custom-surveys.feedbacks.tickets',
        params: {
          question: {
            question_id: question.question_id,
            text: question.text,
          },
        },
      })
    },
    getImageUrl(section, image) {
      return `https://static.teamculture.com.br/images/${section}/${image}`
    },
    getQuestionValue(frequencies, score) {
      if (!frequencies) return 0

      const item = frequencies.find(f => f.score === score)
      const value = !item ? 0 : item.frequency * 100

      return Number(value.toFixed(2))
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~@/styles/custom/colors';

.metric-info {
  margin: 0.5rem 0.6rem 0rem 0rem;
  width: 100%;

  .category-image {
    border-radius: 50%;
    width: 50px;
  }

  .metric-questions {
    color: map-get($theme-colors, 'gray02');

    .question-answers {
      font-size: 0.8rem;
      font-weight: bold;
    }

    .question-label {
      display: flex;
      margin-bottom: 5px;
      align-items: center;

      .category-icon {
        width: 43px !important;
        height: 43px !important;
      }
    }
  }

  .metric-score {
    display: flex;
    justify-content: space-around;
    align-items: center;
  }

  h4 {
    border-radius: 30px;
    color: map-get($theme-colors, 'gray04');
    font-size: 20px;
    font-weight: 500;
    margin-bottom: 0px;

    @media (max-width: 1350px) {
      font-size: 13px;
    }

    @media (max-width: 320px) {
      font-size: 14px;
    }
  }

  p {
    color: map-get($theme-colors, 'gray02');
    font-size: 12px;
    text-decoration: none;
    margin: 0;
  }

  .titles {
    text-align: center;
    color: map-get($theme-colors, 'primary');
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .questions-link {
    text-decoration: underline !important;
    text-transform: capitalize !important;
  }

  .reliability-rate {
    word-wrap: normal;

    .veryLow {
      color: map-get($theme-colors, 'red10');
    }
    .low {
      color: map-get($theme-colors, 'red09');
    }
    .medium {
      color: map-get($theme-colors, 'yellow02');
    }
    .high {
      color: map-get($theme-colors, 'green06');
    }
    .veryHigh {
      color: map-get($theme-colors, 'green05');
    }
  }
}
</style>
